import './comment_editor.scss';

import flatMap from 'lodash/flatMap';
import uniq from 'lodash/uniq';
import memoize from 'memoize-one';
import { arrayOf, func, number, objectOf } from 'prop-types';
import React from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { Button, Col, Row } from 'reactstrap';

import { approvalTeamShape, commentShape, userShape } from '../../prop-types';
import api from '../../REST/api';
import { tapFlashError, tapFlashSuccess } from '../../utils/flash';
import CommentsAttachment from '../CommentAttachments';
import { getMarkdown } from '../mention-utils';

const endpoint = api.addAttachment;

class CommentEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      comment: props.default,
      submitting: false,
      mentionableUsers: this.getMentionables(
        props.usersById,
        props.teamsByName,
      ),
      filteredUsers: [],
      filesToAttach: [],
    };
  }

  handleAttachmentSelection = () => {
    this.setState({ filesToAttach: endpoint.uploader.getFiles() });
  };

  handleTextChange = (event, newValue, newPlainTextValue, mentions) => {
    const { teamsByName } = this.props;
    const { comment } = this.state;
    this.setState(
      {
        comment: {
          ...comment,
          text: newPlainTextValue,
          mentions: flatMap(mentions, libMention => {
            if (teamsByName[libMention.id]) {
              return teamsByName[libMention.id].members.map(id => ({
                userId: id,
                startIndex: libMention.plainTextIndex,
                length: libMention.display.length,
              }));
            }
            return {
              userId: Number.parseInt(libMention.id, 10),
              startIndex: libMention.plainTextIndex,
              length: libMention.display.length,
            };
          }),
        },
      },
      () => this.updateFilteredUsers(),
    );
  };

  handleClick = () => {
    this.setState({ submitting: true });
    this.setupUploaderStore();
    this.props
      .onClick(this.state.comment)
      .then(() => {
        this.setState({
          comment: this.props.default,
          submitting: false,
          filesToAttach: [],
        });
        this.clearUploaderStore();
      })
      .then(
        tapFlashSuccess(
          this.state.comment.id ? 'Comment updated' : 'Comment sent',
        ),
        tapFlashError(),
      );
  };

  getMarkdown = memoize(getMarkdown);

  getMentionables = (usersById = {}, teamsByName = {}) => {
    const { preferableMentions } = this.props;
    //  To handle scenarios where owner and collaborators would be the same.
    const uniquePreferables = uniq(preferableMentions);
    return [].concat(
      uniquePreferables.map(ea => ({
        id: usersById[ea].id,
        display: `@${usersById[ea].firstName} ${usersById[ea].lastName}`,
      })),
      Object.keys(teamsByName).map(name => ({ id: name, display: `@${name}` })),
      Object.values(usersById)
        .filter(item => !uniquePreferables.includes(item.id))
        .map(ea => ({
          id: ea.id,
          display: `@${ea.firstName} ${ea.lastName}`,
        })),
    );
  };

  setupUploaderStore = () => {
    this.state.filesToAttach.map(({ name, data, type }) =>
      endpoint.uploader.addFile({ name, data, type }),
    );
  };

  updateFilteredUsers = () => {
    const { comment, mentionableUsers } = this.state;
    //  To solve the issue where mutliple '@' will occur as this gets the starting value of the last @
    const mentionablesText = comment.text.substring(
      comment.text.lastIndexOf('@'),
      comment.text.length,
    );
    const filteredUsers = mentionableUsers.filter(item =>
      item.display.toLowerCase().startsWith(mentionablesText.toLowerCase()),
    );
    this.setState({
      filteredUsers,
    });
  };

  clearUploaderStore = () => {
    endpoint.uploader.reset();
  };

  render() {
    const { onCancel } = this.props;
    const { comment, submitting, filteredUsers, filesToAttach } = this.state;
    const text = this.getMarkdown(comment.text, comment.mentions);

    const disabled = submitting || text.length === 0;

    return (
      <div className="mt-2">
        <div className="form-control h-auto mb-2 p-0">
          <MentionsInput
            value={text}
            onChange={this.handleTextChange}
            className="c-comment-editor"
          >
            <Mention
              trigger="@"
              data={filteredUsers}
              type="user"
              className="c-comment-editor__highlight--user"
              markup="@[__display__](__id__)"
            />
          </MentionsInput>
        </div>
        <Row className="justify-content-between">
          <Col xs={2}>
            <CommentsAttachment
              uploader={endpoint.uploader}
              onSuccess={this.handleAttachmentSelection}
              onOpened={this.setupUploaderStore}
              count={filesToAttach.length}
              disabled={submitting}
            />
          </Col>
          <Col xs="auto">
            {onCancel && (
              <Button onClick={onCancel} className="mr-1" size="sm">
                Cancel
              </Button>
            )}
            <Button
              onClick={this.handleClick}
              type="button"
              color={text.length === 0 ? 'secondary' : 'primary'}
              size="sm"
              disabled={disabled}
            >
              {comment.id ? 'Update Comment' : 'Comment'}
            </Button>
          </Col>
        </Row>
      </div>
    );
  }
}

CommentEditor.propTypes = {
  onClick: func.isRequired,
  usersById: objectOf(userShape.isRequired).isRequired,
  teamsByName: objectOf(approvalTeamShape.isRequired).isRequired,
  preferableMentions: arrayOf(number),
  default: commentShape,
  onCancel: func,
};

CommentEditor.defaultProps = {
  onCancel: undefined,
  default: { text: '', mentions: [] },
  preferableMentions: [],
};

export default CommentEditor;
